import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { getApiErrorMessage, isCancelledError } from '@/api/errors';

/**
 * Loads data with a fetcher. Pass `null` to disable loading.
 * The fetcher should forward `signal` to the API call so stale requests are cancelled.
 */
export type ResourceFetcher<T> = (signal: AbortSignal) => Promise<T>;

export interface UseResourceOptions {
  /**
   * Message shown when the server does not provide one
   * @default 'Failed to load data'
   */
  errorMessage?: string;
  /**
   * Keep showing the previous data while a new fetcher (e.g. new filters) loads.
   * Set to false for detail views keyed by id.
   * @default true
   */
  keepPreviousData?: boolean;
}

export interface UseResourceResult<T> {
  data: T | undefined;
  /** Local updates after mutations (optimistic or from mutation responses) */
  setData: Dispatch<SetStateAction<T | undefined>>;
  /** User-facing error message of the last load, if it failed */
  error: string | null;
  /** True while loading and there is no data to show yet */
  isLoading: boolean;
  /** True while any request is in flight */
  isFetching: boolean;
  /** Re-run the fetcher */
  reload: () => Promise<void>;
}

/**
 * Generic async data loader with loading/error state and cancellation.
 *
 * - The fetcher's identity is the dependency: memoise it with useCallback and
 *   list the filters it uses, and the resource reloads whenever they change.
 * - A newer request always wins: older requests are aborted and their results
 *   ignored (stale guard), including after unmount.
 *
 * @example
 * ```tsx
 * const fetcher = useCallback((signal) => nodesApi.getAll({ status }, { signal }), [status]);
 * const { data, isLoading, error, reload } = useResource(fetcher);
 * ```
 */
export function useResource<T>(
  fetcher: ResourceFetcher<T> | null,
  options: UseResourceOptions = {}
): UseResourceResult<T> {
  const { errorMessage = 'Failed to load data', keepPreviousData = true } = options;

  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(fetcher !== null);

  const requestIdRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);

  /**
   * Cancel the in-flight request and make sure its result is ignored
   */
  const invalidate = useCallback(() => {
    requestIdRef.current += 1;
    controllerRef.current?.abort();
  }, []);

  const run = useCallback(async () => {
    if (!fetcher) {
      return;
    }

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const requestId = ++requestIdRef.current;

    setIsFetching(true);
    try {
      const result = await fetcher(controller.signal);
      if (requestId !== requestIdRef.current) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (requestId !== requestIdRef.current || isCancelledError(err)) return;
      setError(getApiErrorMessage(err, errorMessage));
    } finally {
      if (requestId === requestIdRef.current) {
        setIsFetching(false);
      }
    }
  }, [fetcher, errorMessage]);

  useEffect(() => {
    if (!fetcher) {
      setIsFetching(false);
      return;
    }

    if (!keepPreviousData) {
      setData(undefined);
      setError(null);
    }

    run();

    // Deps change or unmount: drop the in-flight request
    return invalidate;
  }, [run, fetcher, keepPreviousData, invalidate]);

  return {
    data,
    setData,
    error,
    isLoading: isFetching && data === undefined,
    isFetching,
    reload: run,
  };
}
